import React from 'react';
import { Props, InfoBoxVariant } from './props';
import { clsx } from 'clsx';
import {
  BsInfoCircleFill,
  BsExclamationTriangleFill,
  BsExclamationCircleFill,
  BsCheckCircleFill,
} from 'react-icons/bs';

export const InfoBox: React.FC<Props> = ({
  title,
  children,
  variant = 'info',
  className,
  icon,
}) => {
  // Get the appropriate default icon based on variant
  const getIcon = () => {
    if (icon) return icon;

    switch (variant) {
      case 'warning':
        return <BsExclamationTriangleFill className="text-xl" />;
      case 'error':
        return <BsExclamationCircleFill className="text-xl" />;
      case 'success':
        return <BsCheckCircleFill className="text-xl" />;
      case 'info':
      default:
        return <BsInfoCircleFill className="text-xl" />;
    }
  };

  // Get the appropriate color classes based on variant
  const getColorClasses = (): string => {
    switch (variant) {
      case 'warning':
        return 'bg-amber-50 border-amber-200 text-amber-800';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'info':
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  // Get the appropriate icon color classes based on variant
  const getIconColorClasses = (): string => {
    switch (variant) {
      case 'warning':
        return 'text-amber-500';
      case 'error':
        return 'text-red-500';
      case 'success':
        return 'text-green-500';
      case 'info':
      default:
        return 'text-blue-500';
    }
  };

  return (
    <div className={clsx('flex items-start p-4 rounded-md border', getColorClasses(), className)}>
      <div className={clsx('mr-3 flex-shrink-0 mt-0.5', getIconColorClasses())}>{getIcon()}</div>
      <div className="flex-1">
        {title && <h4 className="font-medium mb-1">{title}</h4>}
        <div className="text-sm">{children}</div>
      </div>
    </div>
  );
};

export type InfoBoxProps = Props;
